import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import API_URL from '../config';

function CompanyPageTab({ user }) {
  const [entries, setEntries] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [creating, setCreating] = useState(false);

  const [displayName, setDisplayName] = useState('');
  const [location, setLocation] = useState('');
  const [referralBonus, setReferralBonus] = useState('');
  const [hiringStatus, setHiringStatus] = useState('hiring');
  const [isPublished, setIsPublished] = useState(false);

  const editorRef = useRef(null);
  const BASE_URL = 'https://dashboard.mountainrecruiting.com';

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/company-pages-for-user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(res.data);
    } catch (err) {
      console.error('Error loading company pages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  useEffect(() => {
    const entry = entries[selectedIdx];
    if (!entry) return;
    const p = entry.page;
    setDisplayName(p?.display_name || entry.company?.name || '');
    setLocation(p?.location || '');
    setReferralBonus(p?.referral_bonus || '');
    setHiringStatus(p?.hiring_status || 'hiring');
    setIsPublished(p?.is_published || false);
    if (editorRef.current) {
      editorRef.current.innerHTML = p?.description || '';
    }
  }, [entries, selectedIdx]);

  const currentEntry = entries[selectedIdx] || null;
  const currentPage = currentEntry?.page || null;
  const currentCompany = currentEntry?.company || null;

  const execFormat = (command) => {
    editorRef.current?.focus();
    document.execCommand(command, false, null);
  };

  const handleCreatePage = async () => {
    if (!currentCompany) return;
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/company-pages`, {
        company_id: currentCompany.id,
        description: '',
        referral_bonus: '',
        hiring_status: 'hiring',
        is_published: false
      }, { headers: { Authorization: `Bearer ${token}` } });
      await loadEntries();
      setSaveMsg('Page created! Fill in the details and publish when ready.');
      setTimeout(() => setSaveMsg(''), 4000);
    } catch (err) {
      alert('Error creating page. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    if (!currentPage) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/company-pages/${currentPage.slug}`, {
        description: editorRef.current?.innerHTML || '',
        referral_bonus: referralBonus,
        hiring_status: hiringStatus,
        is_published: isPublished,
        display_name: displayName,
        location: location
      }, { headers: { Authorization: `Bearer ${token}` } });
      await loadEntries();
      setSaveMsg('Saved successfully!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveMsg('Error saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    const newVal = !isPublished;
    setIsPublished(newVal);
    if (!currentPage) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/company-pages/${currentPage.slug}`, {
        description: editorRef.current?.innerHTML || '',
        referral_bonus: referralBonus,
        hiring_status: hiringStatus,
        is_published: newVal,
        display_name: displayName,
        location: location
      }, { headers: { Authorization: `Bearer ${token}` } });
      await loadEntries();
      setSaveMsg(newVal ? 'Page is now live!' : 'Page unpublished.');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setIsPublished(!newVal);
    }
  };

  const handleCopyLink = () => {
    if (!currentPage) return;
    const url = `${BASE_URL}/companies/${currentPage.slug}`;
    navigator.clipboard.writeText(url);
    setSaveMsg('Link copied!');
    setTimeout(() => setSaveMsg(''), 2000);
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading company pages...</div>;
  }

  if (entries.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
        <p>No companies linked to your account yet.</p>
        <p style={{ fontSize: 13 }}>Contact your Mountain Recruiting representative to get set up.</p>
      </div>
    );
  }

  const toolbarBtn = (label, onClick, title) => (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      style={{
        padding: '4px 10px', border: '1px solid #ddd', borderRadius: 4,
        background: '#f8f8f8', cursor: 'pointer', fontSize: 13, fontWeight: 600,
        color: '#333', lineHeight: 1.4
      }}
    >{label}</button>
  );

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 20px' }}>

      {entries.length > 1 && (
        <div style={{ marginBottom: 24 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>Company</label>
          <select
            value={selectedIdx}
            onChange={e => setSelectedIdx(Number(e.target.value))}
            style={{ padding: '10px 14px', borderRadius: 6, border: '1px solid #ddd', fontSize: 15, minWidth: 260 }}
          >
            {entries.map((e, i) => (
              <option key={e.company.id} value={i}>{e.company.name}</option>
            ))}
          </select>
        </div>
      )}

      {!currentPage ? (
        <div style={{
          background: '#fff', borderRadius: 10, padding: 40, textAlign: 'center',
          border: '1px solid #e8e8e8', boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏢</div>
          <h3 style={{ color: '#1a2e1a', margin: '0 0 8px' }}>No company page yet</h3>
          <p style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>
            Create a public page for <strong>{currentCompany?.name}</strong> to attract driver candidates.
          </p>
          <button
            onClick={handleCreatePage}
            disabled={creating}
            style={{
              background: '#1a2e1a', color: '#fff', border: 'none',
              padding: '12px 28px', borderRadius: 6, fontSize: 15,
              fontWeight: 700, cursor: 'pointer'
            }}
          >
            {creating ? 'Creating...' : 'Create Company Page'}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Status bar */}
          <div style={{
            background: '#fff', borderRadius: 10, padding: '16px 20px',
            border: '1px solid #e8e8e8', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700,
                background: isPublished ? '#e8f5e9' : '#fafafa',
                color: isPublished ? '#2e7d32' : '#999',
                border: `1px solid ${isPublished ? '#a5d6a7' : '#ddd'}`
              }}>
                {isPublished ? '🟢 Live' : '⚫ Draft'}
              </span>
              {isPublished && (
                <a
                  href={`${BASE_URL}/companies/${currentPage.slug}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 13, color: '#1a2e1a', textDecoration: 'underline' }}
                >
                  {BASE_URL}/companies/{currentPage.slug}
                </a>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {isPublished && (
                <button onClick={handleCopyLink} style={{
                  background: '#f0f0f0', color: '#333', border: 'none',
                  padding: '8px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer'
                }}>🔗 Copy Link</button>
              )}
              <button onClick={handleTogglePublish} style={{
                background: isPublished ? '#fff3f3' : '#1a2e1a',
                color: isPublished ? '#c0392b' : '#fff',
                border: isPublished ? '1px solid #f5c6cb' : 'none',
                padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer'
              }}>
                {isPublished ? 'Unpublish' : 'Publish Page'}
              </button>
            </div>
          </div>

          {saveMsg && (
            <div style={{
              padding: '10px 16px', borderRadius: 6, fontSize: 14, fontWeight: 500,
              background: saveMsg.includes('Error') ? '#fde8e8' : '#e8f5e9',
              color: saveMsg.includes('Error') ? '#c0392b' : '#2e7d32',
              border: `1px solid ${saveMsg.includes('Error') ? '#f5c6cb' : '#a5d6a7'}`
            }}>{saveMsg}</div>
          )}

          {/* Edit form */}
          <div style={{
            background: '#fff', borderRadius: 10, padding: 28,
            border: '1px solid #e8e8e8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
          }}>
            <h3 style={{ margin: '0 0 20px', color: '#1a2e1a', fontSize: 17 }}>Page Content</h3>

            {/* Company name */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>
                Company Name
              </label>
              <input
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="Enter company name as it will appear on the page"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 6,
                  border: '1px solid #ddd', fontSize: 14, fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Location */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>
                Location
              </label>
              <input
                value={location}
                onChange={e => setLocation(e.target.value)}
                placeholder="e.g. Atlanta, GA"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 6,
                  border: '1px solid #ddd', fontSize: 14, fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Hiring status */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 8 }}>
                Hiring Status
              </label>
              <div style={{ display: 'flex', gap: 10 }}>
                {['hiring', 'full'].map(val => (
                  <button
                    key={val} type="button" onClick={() => setHiringStatus(val)}
                    style={{
                      padding: '8px 20px', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      background: hiringStatus === val ? (val === 'hiring' ? '#1a2e1a' : '#c0392b') : '#f5f5f5',
                      color: hiringStatus === val ? '#fff' : '#555',
                      border: hiringStatus === val ? 'none' : '1px solid #ddd'
                    }}
                  >
                    {val === 'hiring' ? '🟢 Actively Hiring' : '🔴 Positions Full'}
                  </button>
                ))}
              </div>
            </div>

            {/* Description — rich text */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>
                Company Description / Culture
              </label>
              {/* Toolbar */}
              <div style={{
                display: 'flex', gap: 6, padding: '6px 8px',
                border: '1px solid #ddd', borderBottom: 'none',
                borderRadius: '6px 6px 0 0', background: '#f5f5f5'
              }}>
                {toolbarBtn('B', () => execFormat('bold'), 'Bold (Ctrl+B)')}
                {toolbarBtn('• List', () => execFormat('insertUnorderedList'), 'Bullet list')}
              </div>
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onKeyDown={e => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
                    e.preventDefault();
                    execFormat('bold');
                  }
                }}
                style={{
                  minHeight: 140, padding: '10px 12px',
                  border: '1px solid #ddd', borderRadius: '0 0 6px 6px',
                  fontSize: 14, fontFamily: 'inherit', lineHeight: 1.6,
                  outline: 'none', background: '#fff',
                  boxSizing: 'border-box', width: '100%'
                }}
              />
              <p style={{ fontSize: 12, color: '#999', margin: '5px 0 0' }}>
                Select text then click <strong>B</strong> to bold, or click <strong>• List</strong> to add bullet points.
              </p>
            </div>

            {/* Referral bonus */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>
                Referral Bonus <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span>
              </label>
              <input
                value={referralBonus}
                onChange={e => setReferralBonus(e.target.value)}
                placeholder="e.g. Earn $500 for every driver you refer who gets hired!"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 6,
                  border: '1px solid #ddd', fontSize: 14, fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{ fontSize: 12, color: '#888', margin: '6px 0 0' }}>
                This will appear as a highlighted banner on the public page.
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  background: '#1a2e1a', color: '#fff', border: 'none',
                  padding: '12px 28px', borderRadius: 6, fontSize: 15,
                  fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              {isPublished && (
                <a
                  href={`${BASE_URL}/companies/${currentPage.slug}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ color: '#1a2e1a', fontSize: 14, fontWeight: 600, textDecoration: 'underline' }}
                >
                  Preview live page →
                </a>
              )}
            </div>
          </div>

          <div style={{
            background: '#f0f7f0', borderRadius: 8, padding: '14px 18px',
            border: '1px solid #c8e6c9', fontSize: 13, color: '#444'
          }}>
            <strong style={{ color: '#1a2e1a' }}>ℹ️ Job details are pulled automatically</strong> from your submitted onboarding form
            — schedule, pay, benefits, etc. Only the fields above are manually set here.
          </div>

        </div>
      )}
    </div>
  );
}

export default CompanyPageTab;
